import { useContext, useEffect, useState, useCallback, useMemo } from "react";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import EditOutlined from "@material-ui/icons/EditOutlined";

import { Header, StyledDialog } from "../styles/GerenciamentoContrato";
import { GerenciamentoContratoContext } from "../contexts/GerenciamentoContratoContext";
import { ConfigurationContext } from "../contexts/ConfigurationContext";
import Chip from "../../../../components/basics/Chip";
import Btn from "../../../../components/buttons/Btn";
import Card from "../../../../components/cards/Card";
import { notify } from "../../../../utils/notify";
import SearchSelect from "../../../../components/inputs/SearchSelect";
import SearchDialog from "../../../../patterns/modals/SearchDialog";
import useGerenciamentoContratoController from "../controllers/GerenciamentoContratoController";
import TextFieldErp from "../../../../components/basics/TextFieldErp";
import MaskService from "./../../../../utils/mask/maskService";
import { QueryParamsContext } from "../../../../context/QueryParamsContext";
import { indexTypeContractByCompany } from "../../../../repositories/qerpapi/erp/faturamento/TipoContratosRepository";

import {
  getClassificacaoTributaria,
  getIndicadorOperacao,
  getCodigoTributacao,
  getNbs,
  getPerfilTributario,
  getPerfilTributarioDados,
} from "../../../../repositories/qerpapi/impostos/ImpostosRepository";
import { ModalEdicaoCadastroServico } from "./ModalEdicaoCadastroServico";
import { appendPerfilToOptionsIfMissing } from "../utils/perfilTributarioUtils";

function mergePerfilOptionsModal(baseList, previousList) {
  const base = baseList || [];
  const prev = previousList || [];
  const extras = prev.filter(
    (p) => !base.some((b) => String(b.id) === String(p.id))
  );
  return [...base, ...extras];
}

const ModalAdicionarServico = ({ open, setOpen, locationId }) => {
  const { queryParams } = useContext(QueryParamsContext);
  const {
    selectService,
    setSelectService,
    valueService,
    setValueService,
    amountService,
    setAmountService,
    errorsSubmitTypeService,
    modality,
    classificacaoOptions,
    setClassificacaoOptions,
    indicadorOptions,
    setIndicadorOptions,
    codTributacaoOptions,
    setCodTributacaoOptions,
    nbsOptions,
    setNbsOptions,
    servicosOptions,
    setServicosOptions,
    perfilTributarioLinhaModal,
    setPerfilTributarioLinhaModal,
  } = useContext(GerenciamentoContratoContext);

  const { configuration } = useContext(ConfigurationContext);

  const {
    handleSelectService,
    clearConttextModalTypeService,
    selectionService,
    tofillInInputService,
  } = useGerenciamentoContratoController();
  const [modalTypeServiceOpen, setModalTypeServiceOpen] = useState(false);
  const [edicaoCadastroServicoOpen, setEdicaoCadastroServicoOpen] =
    useState(false);
  const [perfilTributarioOptionsModal, setPerfilTributarioOptionsModal] =
    useState([]);
  const [loadingPerfilTributarioModal, setLoadingPerfilTributarioModal] =
    useState(false);

  const utilizaNovaNfse = useMemo(
    () => configuration?.utiliza_nova_nfse === 1,
    [configuration]
  );

  const fetchData = useCallback(async () => {
    try {
      const [
        servicosData,
        classificacaoData,
        indicadorData,
        codTributacaoData,
        nbsData,
      ] = await Promise.all([
        indexTypeContractByCompany({
          empresa_cnpj: queryParams.empresa_cnpj,
          empresa_id: queryParams.empresa_id,
          where: { ativo: "S" },
        }),
        getClassificacaoTributaria(),
        getIndicadorOperacao(),
        getCodigoTributacao(),
        getNbs(),
      ]);

      setServicosOptions(servicosData.data);
      setClassificacaoOptions(classificacaoData);
      setIndicadorOptions(indicadorData);
      setCodTributacaoOptions(codTributacaoData);
      setNbsOptions(nbsData);
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
    }
  }, [
    queryParams.empresa_cnpj,
    queryParams.empresa_id,
    setServicosOptions,
    setClassificacaoOptions,
    setIndicadorOptions,
    setCodTributacaoOptions,
    setNbsOptions,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadPerfilOptionsModal = useCallback(async () => {
    setLoadingPerfilTributarioModal(true);
    try {
      const list = await getPerfilTributario();

      if (list?.success === false) {
        notify("Erro ao carregar perfis tributários");
        return;
      }

      const rows = Array.isArray(list) ? list : list?.data || [];
      const ativos = rows.filter(
        (row) => row?.ativo === true && row?.nfse === true
      );
      setPerfilTributarioOptionsModal((prev) =>
        mergePerfilOptionsModal(ativos, prev)
      );
    } catch (error) {
      console.error("Erro ao carregar perfis tributários:", error);
    } finally {
      setLoadingPerfilTributarioModal(false);
    }
  }, []);

  const resolvePerfilFallback = useCallback((servico, detalhe) => {
    if (detalhe?.descricao || detalhe?.configuracaoNfse) {
      return detalhe;
    }

    const embedded = servico?.perfil_tributario;
    if (embedded?.id || embedded?.descricao) {
      return embedded;
    }

    const perfilId = servico?.perfil_tributario_id;
    if (!perfilId) return null;

    return {
      id: perfilId,
      descricao: "Perfil vinculado",
    };
  }, []);

  const syncPerfilLinhaFromServico = useCallback(
    async (servico) => {
      const perfilId = servico?.perfil_tributario_id;
      if (!perfilId) {
        setPerfilTributarioLinhaModal(null);
        return;
      }

      const embedded = servico?.perfil_tributario;
      if (embedded?.descricao || embedded?.configuracaoNfse) {
        setPerfilTributarioLinhaModal(embedded);
        appendPerfilToOptionsIfMissing(
          setPerfilTributarioOptionsModal,
          embedded
        );
        if (embedded?.configuracaoNfse) return;
      }

      try {
        const detalhe = await getPerfilTributarioDados(perfilId);
        const perfilValido =
          detalhe && detalhe.success !== false ? detalhe : null;
        const perfilResolvido = resolvePerfilFallback(servico, perfilValido);

        setPerfilTributarioLinhaModal(perfilResolvido);
        appendPerfilToOptionsIfMissing(
          setPerfilTributarioOptionsModal,
          perfilResolvido
        );
      } catch (error) {
        console.error("Erro ao carregar perfil tributário:", error);
        const fallback = resolvePerfilFallback(servico, null);
        setPerfilTributarioLinhaModal(fallback);
        appendPerfilToOptionsIfMissing(
          setPerfilTributarioOptionsModal,
          fallback
        );
      }
    },
    [
      setPerfilTributarioLinhaModal,
      setPerfilTributarioOptionsModal,
      resolvePerfilFallback,
    ]
  );

  const fecharEdicaoCadastroServico = useCallback(
    () => setEdicaoCadastroServicoOpen(false),
    []
  );

  useEffect(() => {
    if (!open || !utilizaNovaNfse) return;
    loadPerfilOptionsModal();
  }, [open, utilizaNovaNfse, loadPerfilOptionsModal]);

  useEffect(() => {
    if (!open || !utilizaNovaNfse) return;
    syncPerfilLinhaFromServico(selectService);
  }, [open, utilizaNovaNfse, selectService, syncPerfilLinhaFromServico]);

  const findOptionById = (id, options) => {
    if (!id || !options) return null;
    return options.find((opt) => opt.id === id);
  };

  const findOptionByCode = (codigo, options) => {
    if (!codigo || !options) return null;
    return options.find((opt) => opt.codigo === codigo);
  };

  const columnsTypeServiceModal = [
    { label: "Descrição", id: "descricao" },
    {
      label: "Valor",
      id: "valor",
      format: (item) =>
        `R$ ${MaskService.toMask(
          "decimal",
          parseFloat(item.valor).toFixed(
            configuration?.contract_decimal_size || 2
          ),
          configuration?.contract_decimal_size || 2
        )}`,
    },
    { label: "Modalidade", id: "tiposervico" },
    {
      label: "LC 116",
      id: "tipocontratos_id",
      format: (item) => {
        const opt = findOptionById(item.tipocontratos_id, servicosOptions);
        const code = opt
          ? opt.codigo || opt.nfse_codigo
          : item.tipocontratos_id;

        return (
          <Tooltip title="Código Serviço LC116" placement="top">
            <span>{code}</span>
          </Tooltip>
        );
      },
    },
    {
      label: "NBS",
      id: "nbs_codigo",
      format: (item) => {
        const opt = findOptionByCode(item.nbs_codigo, nbsOptions);
        const code = opt ? opt.codigo : item.nbs_codigo;

        return (
          <Tooltip title="Nomenclatura Brasileira de Serviços" placement="top">
            <span>{code}</span>
          </Tooltip>
        );
      },
    },
    {
      label: "INDOP",
      id: "indicador_operacao_codigo",
      format: (item) => {
        const opt = findOptionByCode(
          item.indicador_operacao_codigo,
          indicadorOptions
        );
        const code = opt ? opt.codigo : item.indicador_operacao_codigo;

        return (
          <Tooltip title="Indicador de Operação de Consumo" placement="top">
            <span>{code}</span>
          </Tooltip>
        );
      },
    },
    {
      label: "cClasstrib IBS/CBS",
      id: "classificacao_tributaria_codigo",
      format: (item) => {
        const opt = findOptionByCode(
          item.classificacao_tributaria_codigo,
          classificacaoOptions
        );
        const code = opt ? opt.codigo : item.classificacao_tributaria_codigo;

        return (
          <Tooltip title="Classificação Tributária IBS/CBS" placement="top">
            <span>{code}</span>
          </Tooltip>
        );
      },
    },
    {
      label: "Cód. Trib. Nac.",
      id: "tributacao_nacional_codigo",
      format: (item) => {
        const opt = findOptionByCode(
          item.tributacao_nacional_codigo,
          codTributacaoOptions
        );
        const code = opt ? opt.codigo : item.tributacao_nacional_codigo;

        return (
          <Tooltip title="Código de Tributação Nacional" placement="top">
            <span>{code}</span>
          </Tooltip>
        );
      },
    },
  ];

  useEffect(() => {
    if (!locationId && locationId !== 0) return;
    tofillInInputService(locationId);
  }, [locationId, tofillInInputService]);

  const handleClose = () => {
    clearConttextModalTypeService();
    setOpen(false);
  };

  const handleSelectionService = async () => {
    const callback = await selectionService(locationId);
    if (callback?.success) handleClose();
  };

  return (
    <>
      {edicaoCadastroServicoOpen && (
        <ModalEdicaoCadastroServico
          open={edicaoCadastroServicoOpen}
          onClose={fecharEdicaoCadastroServico}
          locationId={locationId}
          perfilTributarioOptions={perfilTributarioOptionsModal}
          setPerfilTributarioOptions={setPerfilTributarioOptionsModal}
          loadingPerfilTributario={loadingPerfilTributarioModal}
        />
      )}
      {modalTypeServiceOpen && (
        <SearchDialog
          open={modalTypeServiceOpen}
          setOpen={setModalTypeServiceOpen}
          columns={columnsTypeServiceModal}
          selectable={true}
          multiSelect={false}
          handleMainClick={handleSelectService}
          route="faturamento/contratos/v1/contrato-servicos-empresa"
          rawUrl={`servico_tipo=${modality?.value}&empresa_id=${queryParams.empresa_id}`}
          title="CLASSIFICAÇÃO DO SERVIÇO"
          urlFilters="descricao"
        />
      )}
      <Dialog
        fullWidth={true}
        maxWidth="md"
        open={open}
        onClose={handleClose}
        aria-labelledby="dialog-adicionar-servico"
      >
        <StyledDialog>
          <DialogTitle id="dialog-title">
            <Header>
              <Grid container align="center" justifyContent="center">
                <Chip
                  bgcolor="tertiary"
                  textcolor="white"
                  label="ADICIONAR SERVIÇOS AO CONTRATO"
                />
              </Grid>
            </Header>
          </DialogTitle>
          <DialogContent>
            <Card chipcolor="transparent" fullWidth={true}>
              <Grid item container xs spacing={2}>
                <Grid item xs>
                  <Grid
                    container
                    spacing={1}
                    alignItems="flex-end"
                    wrap="nowrap"
                  >
                    <Grid item xs style={{ minWidth: 0, flex: 1 }}>
                      <SearchSelect
                        descriptionField="descricao"
                        label="Serviço"
                        value={selectService}
                        setValue={setSelectService}
                        filter="descricao"
                        urlFilters="descricao"
                        route="faturamento/contratos/v1/contrato-servicos-empresa"
                        rawUrl={`servico_tipo=${modality?.value}&empresa_id=${queryParams.empresa_id}`}
                        variant="filled"
                        customChange
                        onChange={(value) => {
                          setSelectService(value);
                          setPerfilTributarioLinhaModal(null);
                          setValueService(
                            MaskService.toMask(
                              "decimal",
                              parseFloat(value?.valor || "0.00000").toFixed(
                                configuration?.contract_decimal_size || 2
                              ),
                              configuration?.contract_decimal_size || 2
                            )
                          );
                        }}
                        error={
                          errorsSubmitTypeService?.selectService ? true : false
                        }
                        disabled={locationId !== null}
                      />
                    </Grid>
                    {utilizaNovaNfse && (
                      <Grid item>
                        <Tooltip
                          title="Editar perfil tributário (cadastro do serviço)"
                          placement="top"
                        >
                          <span>
                            <IconButton
                              onClick={() => setEdicaoCadastroServicoOpen(true)}
                              disabled={!selectService}
                            >
                              <EditOutlined />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid item xs={1}>
                  {locationId === null && (
                    <Btn
                      label={<SearchIcon />}
                      full="true"
                      onClick={() => setModalTypeServiceOpen(true)}
                      bgcolor="tertiary"
                    />
                  )}
                </Grid>
                <Grid item xs={2}>
                  <TextFieldErp
                    label="Vl. do Serviço"
                    value={valueService}
                    setValue={setValueService}
                    size="small"
                    mask="decimal"
                    decimals={configuration?.contract_decimal_size || 2}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextFieldErp
                    label="Quantidade"
                    value={amountService}
                    setValue={setAmountService}
                    size="small"
                    type="number"
                    onInput={(e) => {
                      e.target.value = Math.max(0, parseInt(e.target.value))
                        .toString()
                        .slice(0, 12);
                    }}
                    error={
                      errorsSubmitTypeService?.amountService ? true : false
                    }
                  />
                </Grid>
                {utilizaNovaNfse && selectService && (
                  <Grid item xs={12}>
                    <TextFieldErp
                      label="Perfil tributário"
                      value={
                        perfilTributarioLinhaModal?.descricao ||
                        selectService?.perfil_tributario?.descricao ||
                        (selectService?.perfil_tributario_id
                          ? "Perfil vinculado"
                          : "Não selecionado")
                      }
                      size="small"
                      disabled
                    />
                  </Grid>
                )}
              </Grid>
            </Card>
          </DialogContent>
          <DialogActions>
            <Btn onClick={handleClose} bgcolor="darkred" label="Cancelar" />
            <Btn
              onClick={handleSelectionService}
              bgcolor="primary"
              label="Adicionar"
            />
          </DialogActions>
        </StyledDialog>
      </Dialog>
    </>
  );
};

export { ModalAdicionarServico };
